import Player from "@vimeo/player";

class SectionVideoCarousel extends HTMLElement {
  constructor() {
    super();
    this.data = scrapeSectionData(this);
    this.isMobile = window.matchMedia("(max-width: 576px)");
    this.carousel = this.querySelector("[data-video-carousel]");
    this.nav = this.querySelector(".video-carousel__nav");
    this.swiper = null;
    this.currentSlide = -1;
    this.slideDelayTimeout = null;
    this.players = new Map();

    if (Shopify.designMode) {
      document.addEventListener("shopify:section:load", (event) => {
        window.loadSwiper.then((swiperLib) => {
          this.swiperLib = swiperLib;
          this.initFunction();
        });
      });
    }

    window.loadSwiper.then((swiperLib) => {
      this.swiperLib = swiperLib;
      this.initFunction();
    });

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this.handleSlide("next");
          observer.unobserve(this);
        }
      },
      {
        rootMargin: "0px 0px -50% 0px",
      }
    );

    observer.observe(this);
  }

  slideTo(index, cb) {
    if (!this.swiper) return;

    const slide = () => {
      this.ignoreChangeEvent = true;
      this.swiper.slideTo(index);
      cb();
    };

    if (this.isMobile.matches) {
      this.slideDelayTimeout = setTimeout(() => {
        slide();
      }, 1000);
    } else {
      slide();
    }
  }

  handleSlide(direction) {
    const allSlides = this.swiper.slides;
    const lastSlide = allSlides.length - 1;

    let previousIndex = this.currentSlide;
    this.currentSlide =
      direction === "prev" ? this.currentSlide - 1 : this.currentSlide + 1;

    this.currentSlide = this.currentSlide > lastSlide ? 0 : this.currentSlide;
    this.currentSlide = this.currentSlide < 0 ? 0 : this.currentSlide;

    let currentIndex = this.currentSlide;
    let slideTo = currentIndex == lastSlide ? 0 : currentIndex + 1;
    // currentIndex = this.isMobile.matches ? currentIndex - 1 : currentIndex;
    currentIndex = currentIndex < 0 ? 0 : currentIndex;
    const activeSlide = allSlides[currentIndex];
    const previousSlide = allSlides[previousIndex];

    if (previousSlide?.classList.contains("vimeo")) {
      const iframe = previousSlide.querySelector("iframe");
      const player = this.players.get(iframe);
      player?.pause();
    }

    if (previousSlide?.classList.contains("cdn")) {
      const slideVideo = previousSlide.querySelector("video");
      slideVideo.pause();
    }

    if (activeSlide.classList.contains("slide__image")) {
      const imageChange = setTimeout(() => {
        this.slideTo(slideTo, () => this.handleSlide("next"));
      }, 2000);
      this.swiper.on("sliderMove", () => {
        clearTimeout(imageChange);
      });
    } else {
      if (activeSlide.classList.contains("vimeo")) {
        const iframe = activeSlide.querySelector("iframe");
        const player = this.players.get(iframe) || new Player(iframe);
        player.off("ended");
        player.on("ended", () => {
          this.slideTo(slideTo, () => this.handleSlide("next"));
        });
        // If player exists, we assume video has already played and
        // do fade-to-reset transition
        if (this.players.has(iframe)) {
          const onFadeOut = () => {
            iframe.removeEventListener("transitionend", onFadeOut);

            const onFadeIn = () => {
              iframe.removeEventListener("transitionend", onFadeIn);
              player.play();
            };

            iframe.addEventListener("transitionend", onFadeIn);
            player.setCurrentTime(0);
            iframe.style.opacity = 1;
          };

          iframe.addEventListener("transitionend", onFadeOut);
          iframe.style.opacity = 0;
        } else {
          this.players.set(iframe, player);
          player.play();
        }
      } else {
        const slideVideo = activeSlide.querySelector("video");
        const onEnded = (e) => {
          e.stopImmediatePropagation();
          this.slideTo(slideTo, () => this.handleSlide("next"));
        };
        slideVideo.removeEventListener("ended", onEnded);
        slideVideo.addEventListener("ended", onEnded);

        if (slideVideo.currentTime !== 0) {
          const onFadeOut = () => {
            slideVideo.removeEventListener("transitionend", onFadeOut);

            const onFadeIn = () => {
              slideVideo.removeEventListener("transitionend", onFadeIn);
              slideVideo.play();
            };

            slideVideo.addEventListener("transitionend", onFadeIn);
            slideVideo.currentTime = 0;
            slideVideo.style.opacity = 1;
          };

          slideVideo.addEventListener("transitionend", onFadeOut);
          slideVideo.style.opacity = 0;
        } else {
          slideVideo.play();
        }
      }
    }
  }

  initFunction() {
    if (!this.carousel) return;

    const { Swiper } = this.swiperLib;

    const params = {
      slidesPerView: 1,
      spaceBetween: 0,
      touchEventsTarget: "wrapper",
      direction: "horizontal",
      breakpoints: {
        768: {
          spaceBetween: 20,
          slidesPerView: "auto",
        },
        992: {
          spaceBetween: 20,
          slidesPerView: "auto",
          allowTouchMove: false,
        },
        1600: {
          slidesPerView: "auto",
          spaceBetween: 20,
          allowTouchMove: false,
        },
      },
    };

    const userArrows = this.querySelectorAll(".swiper-arrow");
    userArrows.forEach((arrow) => {
      arrow.addEventListener("click", () => {
        if (arrow.classList.contains("swiper-arrow-next")) {
          let cap = this.swiper.slides.length;
          this.slideTo(
            this.currentSlide + 1 >= cap ? 0 : this.currentSlide + 1,
            () => this.handleSlide("next")
          );
        } else {
          this.slideTo(this.currentSlide <= 0 ? 0 : this.currentSlide - 1, () =>
            this.handleSlide("prev")
          );
        }
      });
    });

    this.swiper = new Swiper(this.carousel, params);

    this.swiper.on("slideChange", () => {
      // For some reason `swiper.slideTo(a, b, false) is still
      // firing this change event, so we have to manually ignore
      // changes triggered by the auto-advancing. We only want to
      // act on slideChange events caused by swiping/user input.
      clearTimeout(this.slideDelayTimeout);

      if (this.ignoreChangeEvent) {
        this.ignoreChangeEvent = false;
        return;
      }

      this.handleSlide(this.swiper.swipeDirection);
    });

    this.swiper.on("lock", () => {
      this.nav.style.display = "none";
    });
    this.swiper.on("unlock", () => {
      this.nav.style.display = null;
    });

    this.nav.style.display = this.swiper.isLocked ? "none" : null;

    if (Shopify.designMode) {
      document.addEventListener("shopify:section:load", (event) => {
        this.swiper.destroy();
      });
    }
  }
}

if (!customElements.get("section-video-carousel")) {
  customElements.define("section-video-carousel", SectionVideoCarousel);
}
